/**
 * Traffic light. Screen spec 4.2. One per project, ever.
 *
 * The precedence table is a pure function on purpose, so the question "why is
 * my project amber" has one answer that can be read, argued about and tested,
 * instead of nine conditionals spread across a screen.
 *
 * The sentence carries the whole meaning. Turn every color off and the band
 * still answers the only question this product exists to answer, which is
 * whether the app is fine.
 *
 * The band's action is the screen's one ink block, and its identity follows
 * the lamp: it reads "Visit your app" while the lamp is green and becomes
 * "See what happened" the moment the lamp changes. Nina never has to evaluate
 * a choice, because there is only ever one.
 */

import { Button } from '../primitives/Button';
import { Lamp } from './StatusPill';
import { t } from '../../lib/i18n/t';
import type { PauseReason, ProjectStatus, StatusValue } from '../../lib/types';
import '../../styles/components/pill.css';

/**
 * The thresholds for rules 6 and 7, flagged in the spec for product sign off
 * and put on screen as numbers so they can be argued about with something
 * concrete rather than in the abstract.
 *
 * The request floor is the part that matters: without it a three visitor app
 * goes amber on a single 404.
 */
export const APPROACHING_AT = 0.8;
export const ERRORS_RATE = 0.05;
export const ERRORS_ABSOLUTE = 10;
export const ERRORS_REQUEST_FLOOR = 20;

export type TrafficReason =
  | 'down'
  | 'provisioning'
  | 'paused'
  | 'deploy-failed'
  | 'add-on-failed'
  | 'over-limit'
  | 'approaching-limit'
  | 'errors-elevated'
  | 'healthy-traffic'
  | 'healthy-quiet'
  | 'ready';

export interface TrafficInput {
  status: ProjectStatus;
  /** Why a 'paused' project is paused. Absent reads as the owner's own pause. */
  pausedReason?: PauseReason | null;
  /** How long every request has been coming back with an error. */
  downMinutes: number;
  lastDeployFailed: boolean;
  /** The date the currently running version shipped, already formatted. */
  runningSince: string;
  /** The add-on that did not finish setting up, by its product name. */
  failedAddOn: string | null;
  /** The resource that is over its limit, by its product name. */
  overLimitResource: string | null;
  approaching: { resource: string; used: string; limit: string } | null;
  errorsLastHour: number;
  requestsLastHour: number;
  hasTraffic: boolean;
  /** Whether the project has had any deploys yet. False for a brand new project. */
  hasDeploys?: boolean;
}

export interface TrafficVerdict {
  reason: TrafficReason;
  /** Which lamp shape and tone to light. */
  lamp: StatusValue;
  /** The eyebrow word beside the lamp. Never dropped. */
  word: string;
  sentence: string;
  /**
   * The band's one ink block. Null when there is genuinely nothing to do
   * yet, a project whose infrastructure is still being built is the only
   * such case, and a button that does nothing is worse than no button.
   */
  action: string | null;
}

export function errorsAreElevated(errors: number, requests: number): boolean {
  if (requests < ERRORS_REQUEST_FLOOR) return false;
  return errors >= ERRORS_ABSOLUTE || errors / requests >= ERRORS_RATE;
}

/** The precedence table from screen spec 4.2, in order, first match wins. */
export function trafficVerdict(input: TrafficInput): TrafficVerdict {
  // Above 'down', and deliberately: a project whose infrastructure is still
  // being built has no deploys, no traffic and no health to report, so every
  // rule below this one would be answering a question about an app that does
  // not exist yet. The band said "Ready" here, which is the specific lie this
  // rule exists to stop.
  if (input.status === 'provisioning') {
    return {
      reason: 'provisioning',
      lamp: 'provisioning',
      word: t('Setting up'),
      sentence: t("We're building your app's database, storage and web address. This takes a minute or two."),
      action: null,
    };
  }

  if (input.status === 'down') {
    return {
      reason: 'down',
      lamp: 'down',
      word: t('Down'),
      // The spec writes this sentence with {n} minutes. One minute is a real
      // case and "the last 1 minutes" is the sentence a person stops trusting,
      // so the singular gets its own key rather than a plural rule engine.
      sentence:
        input.downMinutes === 1
          ? t('Your app is down. Every request in the last minute came back with an error.')
          : t('Your app is down. Every request in the last {n} minutes came back with an error.', {
              n: input.downMinutes,
            }),
      action: t('Put the last working version back'),
    };
  }

  if (input.status === 'paused') {
    // A quota pause is not a switch the owner flipped, so the band sends them
    // to Usage, where adding credits is, not to a resume they cannot use.
    if (input.pausedReason === 'quota') {
      return {
        reason: 'paused',
        lamp: 'paused',
        word: t('Paused'),
        sentence: t(
          'This project is paused because your account used up its allowance. Your web address shows a holding page.',
        ),
        action: t('See usage'),
      };
    }
    return {
      reason: 'paused',
      lamp: 'paused',
      word: t('Paused'),
      sentence: t('This project is paused. Your web address shows a holding page.'),
      action: t('Turn it back on'),
    };
  }

  if (input.lastDeployFailed) {
    return {
      reason: 'deploy-failed',
      lamp: 'didnt-work',
      word: t("Didn't work"),
      sentence: t(
        "Your last deploy didn't work. Your app is still running the version from {date}.",
        { date: input.runningSince },
      ),
      action: t('See what happened'),
    };
  }

  if (input.failedAddOn) {
    return {
      reason: 'add-on-failed',
      lamp: 'didnt-work',
      word: t("Didn't work"),
      sentence: t("Your {addOn} didn't finish setting up.", { addOn: input.failedAddOn }),
      action: t('Try again'),
    };
  }

  if (input.overLimitResource) {
    return {
      reason: 'over-limit',
      lamp: 'didnt-work',
      word: t('Attention'),
      sentence: t("You're over your {resource} limit.", { resource: input.overLimitResource }),
      action: t('See usage'),
    };
  }

  if (input.approaching) {
    return {
      reason: 'approaching-limit',
      lamp: 'didnt-work',
      word: t('Attention'),
      sentence: t("You're close to your {resource} limit. {used} of {limit} used.", {
        resource: input.approaching.resource,
        used: input.approaching.used,
        limit: input.approaching.limit,
      }),
      action: t('See usage'),
    };
  }

  if (errorsAreElevated(input.errorsLastHour, input.requestsLastHour)) {
    return {
      reason: 'errors-elevated',
      lamp: 'didnt-work',
      word: t('Attention'),
      sentence:
        input.errorsLastHour === 1
          ? t('Your app returned errors on 1 request in the last hour.')
          : t('Your app returned errors on {n} requests in the last hour.', {
              n: input.errorsLastHour,
            }),
      action: t('See errors'),
    };
  }

  if (input.hasDeploys === false) {
    return {
      reason: 'ready',
      lamp: 'live',
      word: t('Ready'),
      sentence: t('SV Cloud is ready for your first deploy.'),
      action: t('Visit your app'),
    };
  }

  if (input.hasTraffic) {
    return {
      reason: 'healthy-traffic',
      lamp: 'live',
      word: t('Live'),
      sentence: t("Everything's fine. No problems in the last 24 hours."),
      action: t('Visit your app'),
    };
  }

  return {
    reason: 'healthy-quiet',
    lamp: 'live',
    word: t('Live'),
    sentence: t("Everything's fine. No visitors yet."),
    action: t('Visit your app'),
  };
}

const TONE: Record<StatusValue, string> = {
  live: 'live',
  ready: 'live',
  provisioning: 'building',
  deploying: 'building',
  'didnt-work': 'attention',
  down: 'down',
  paused: 'idle',
  replaced: 'idle',
  skipped: 'idle',
  'cleaning-up': 'building',
};

export interface TrafficLightProps {
  input: TrafficInput;
  onAction?: () => void;
}

export function TrafficLight({ input, onAction }: TrafficLightProps) {
  const verdict = trafficVerdict(input);

  return (
    <section
      className="traffic"
      data-tone={TONE[verdict.lamp]}
      role="status"
      aria-live="polite"
      aria-label={t('Whether your app is fine')}
    >
      <span className="traffic__lamp">
        <Lamp value={verdict.lamp} size="lg" />
      </span>
      <div className="traffic__say">
        <span className="traffic__word">{verdict.word}</span>
        <p className="traffic__sentence">{verdict.sentence}</p>
      </div>
      {verdict.action !== null ? (
        <div className="traffic__action">
          <Button variant="solid" size="lg" onClick={onAction}>
            {verdict.action}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
